
import { Industry, OrganizationSize, OrganizationType } from "@/types/assessment";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Globe, Building2 } from "lucide-react";
import { countries } from "countries-list";

interface OrganizationDetailsSectionProps {
  formState: {
    name: string;
    industry: Industry;
    size: OrganizationSize;
    country: string;
    organizationType: OrganizationType;
  };
  setFormState: (value: any) => void;
}

// List of commonly recognized countries (UN member states and widely recognized territories)
const commonCountries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", 
  "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", 
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", 
  "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", 
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", 
  "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", 
  "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", 
  "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", 
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", 
  "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", 
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", 
  "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", 
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", 
  "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", 
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", 
  "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", 
  "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", 
  "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", 
  "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", 
  "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", 
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", 
  "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", 
  "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", 
  "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", 
  "Zambia", "Zimbabwe"
];

const OrganizationDetailsSection = ({ formState, setFormState }: OrganizationDetailsSectionProps) => {
  // Sort the countries alphabetically
  const sortedCountries = [...commonCountries].sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Organization Details</h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Organization Name *
        </label>
        <Input
          type="text"
          value={formState.name}
          onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter organization name"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Industry *
        </label>
        <Select
          value={formState.industry}
          onValueChange={(value: Industry) => 
            setFormState(prev => ({ ...prev, industry: value }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select industry" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Retail & E-commerce">Retail & E-commerce</SelectItem>
            <SelectItem value="Education & EdTech">Education & EdTech</SelectItem>
            <SelectItem value="Healthcare & Pharma">Healthcare & Pharma</SelectItem>
            <SelectItem value="Financial Services">Financial Services</SelectItem>
            <SelectItem value="Manufacturing & Industry">Manufacturing & Industry</SelectItem>
            <SelectItem value="Government & Public Sector">Government & Public Sector</SelectItem>
            <SelectItem value="Real Estate">Real Estate</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Organization Size *
        </label>
        <Select
          value={formState.size}
          onValueChange={(value: OrganizationSize) => 
            setFormState(prev => ({ ...prev, size: value }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1-10 staff">1-10 staff</SelectItem>
            <SelectItem value="11-100 staff">11-100 staff</SelectItem>
            <SelectItem value="101-1,000 staff">101-1,000 staff</SelectItem>
            <SelectItem value="1,001-10,000 staff">1,001-10,000 staff</SelectItem>
            <SelectItem value="10,001-100,000 staff">10,001-100,000 staff</SelectItem>
            <SelectItem value="More than 100,000 staff">More than 100,000 staff</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Country
        </label>
        <Input
          type="text"
          value={formState.country}
          onChange={(e) => setFormState(prev => ({ ...prev, country: e.target.value }))}
          placeholder="Enter country"
          list="countries"
        />
        <datalist id="countries">
          {sortedCountries.map((country, index) => (
            <option key={index} value={country} />
          ))}
        </datalist>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Organization Type
        </label>
        <Select
          value={formState.organizationType}
          onValueChange={(value: OrganizationType) => 
            setFormState(prev => ({ ...prev, organizationType: value }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Select organization type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Government">Government</SelectItem>
            <SelectItem value="Private - For Profit">Private - For Profit</SelectItem>
            <SelectItem value="Non Profit">Non Profit</SelectItem>
            <SelectItem value="Academic Institution">Academic Institution</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default OrganizationDetailsSection;
